import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import processService from '../services/processService';
import processTypeService from '../services/processTypeService';

const NewProcess = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [typeIds, setTypeIds] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    version: '1.0',
    typeId: '',
    workflowFile: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch process types
        const types = await processTypeService.getAll();
        setTypeIds(types);

        // If in edit mode, fetch process data
        if (isEditMode) {
          const process = await processService.getById(id);
          setFormData({
            code: process.id || '',
            description: process.description || '',
            version: process.version || '1.0',
            typeId: process.processTypeId || '',
            workflowFile: null
          });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const currentVersion = parseFloat(formData.version);
      const newVersion = (currentVersion + 0.1).toFixed(1);
      
      setFormData(prev => ({
        ...prev,
        workflowFile: file,
        version: newVersion
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const formDataToSend = new FormData();
      formDataToSend.append('code', formData.code);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('version', formData.version);
      formDataToSend.append('typeId', formData.typeId);
      if (formData.workflowFile) {
        formDataToSend.append('workflowFile', formData.workflowFile);
      }

      if (isEditMode) {
        await processService.update(id, formDataToSend);
      } else {
        await processService.create(formDataToSend);
      }

      navigate('/processes');
    } catch (err) {
      console.error('Error saving process:', err);
      if (err.response?.status === 409) {
        setError('Impossibile eliminare il processo: esistono codex associati.');
      } else {
        setError('Failed to save process. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.code) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedTypeId = typeIds.find(pt => pt.id === formData.typeId);
  const fileType = selectedTypeId?.id === 'BPMN' ? '.bpmn' : '.json';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {isEditMode ? 'Modifica Processo' : 'Nuovo Processo'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/processes')}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Codice"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                required
                disabled={isEditMode}
                helperText={isEditMode ? "Il codice non può essere modificato" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Versione"
                name="version"
                value={formData.version}
                disabled={true}
                helperText="La versione viene incrementata automaticamente ad ogni upload di file"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tipo Processo</InputLabel>
                <Select
                  name="typeId"
                  value={formData.typeId}
                  label="Tipo Processo"
                  onChange={handleInputChange}
                  required
                  disabled={isEditMode}
                  renderValue={(selected) => {
                    const type = typeIds.find(t => t.id === selected);
                    return type ? (
                      <Chip 
                        label={type.id} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    ) : 'Seleziona un tipo';
                  }}
                >
                  {typeIds.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      <Chip 
                        label={type.id} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    </MenuItem>
                  ))}
                </Select>
                {isEditMode && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Il tipo di processo non può essere modificato
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrizione"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                fullWidth
                disabled={!formData.typeId || formData.typeId.length <= 0}
              >
                Carica File { formData.typeId === 'BPMN' ? '.bpmn' : formData.typeId === 'JSON' ? '.json': '?' }
                <input
                  type="file"
                  hidden
                  accept={ formData.typeId === 'BPMN' ? '.bpmn' : formData.typeId === 'JSON' ? '.json': '*' }
                  onChange={handleFileUpload}
                />
              </Button>
              {formData.workflowFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  File selezionato: {formData.workflowFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default NewProcess; 